(() => {
  const selector = '.lc-etrusted-lazy';
  const linkedProductReviewsSelector = `${selector}[data-lc-etrusted-linked-reviews="true"]`;
  const storedWidgets = new WeakMap();

  const createPlaceholder = (widget) => {
    if (widget.dataset.lcEtrustedActive === 'true') {
      return null;
    }

    const placeholder = document.createElement('span');
    const height = Math.round(widget.getBoundingClientRect().height);
    placeholder.className = 'lc-etrusted-lazy';
    placeholder.style.display = 'block';
    placeholder.style.width = '100%';
    placeholder.style.minHeight = `${Math.max(height, widget.closest('#etrusted-review_carousel_service') ? 320 : 24)}px`;

    for (const attribute of widget.attributes) {
      if (attribute.name.startsWith('data-')) {
        placeholder.setAttribute(attribute.name, attribute.value);
      }
    }

    storedWidgets.set(placeholder, widget);
    widget.replaceWith(placeholder);
    return placeholder;
  };

  const restoreWidget = (placeholder) => {
    if (!placeholder.isConnected || placeholder.dataset.lcEtrustedLoaded === 'true') {
      return;
    }

    placeholder.dataset.lcEtrustedLoaded = 'true';
    const widget = storedWidgets.get(placeholder) || document.createElement('etrusted-widget');
    widget.dataset.lcEtrustedActive = 'true';

    for (const attribute of placeholder.attributes) {
      if (attribute.name.startsWith('data-') && !attribute.name.startsWith('data-lc-etrusted-')) {
        widget.setAttribute(attribute.name, attribute.value);
      }
    }

    placeholder.replaceWith(widget);
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.unobserve(entry.target);
          restoreWidget(entry.target);
        }
      }, { rootMargin: '600px 0px' })
    : null;

  const scheduleLinkedProductReviews = (placeholder) => {
    if (placeholder.dataset.lcEtrustedScheduled === 'true') {
      return;
    }

    placeholder.dataset.lcEtrustedScheduled = 'true';

    const activate = () => restoreWidget(placeholder);
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(activate, { timeout: 1200 });
      } else {
        window.setTimeout(activate, 800);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  };

  const observe = (placeholder) => {
    if (placeholder.dataset.lcEtrustedObserved === 'true') {
      return;
    }

    placeholder.dataset.lcEtrustedObserved = 'true';
    placeholder.style.display ||= 'block';
    placeholder.style.width ||= '100%';
    placeholder.style.minHeight ||= '24px';

    if (placeholder.matches(linkedProductReviewsSelector)) {
      scheduleLinkedProductReviews(placeholder);
      return;
    }

    if (observer) {
      observer.observe(placeholder);
    } else {
      restoreWidget(placeholder);
    }
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches('etrusted-widget')) {
      const placeholder = createPlaceholder(root);
      if (placeholder) observe(placeholder);
      return;
    }

    if (root instanceof Element && root.matches(selector)) {
      observe(root);
    }

    root.querySelectorAll?.('etrusted-widget:not([data-lc-etrusted-active="true"])').forEach((widget) => {
      const placeholder = createPlaceholder(widget);
      if (placeholder) observe(placeholder);
    });
    root.querySelectorAll?.(selector).forEach(observe);
  };

  scan();

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          scan(node);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
